<section class="w-full bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-0" style="height: 52px;">
  <div class="flex items-center justify-between h-full">

    <!-- Left: System Status -->
    <div class="flex items-center gap-5">
      <div class="flex items-center gap-2">
        <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Time</span>
        <span class="text-[11px] text-slate-600 dark:text-slate-400 font-medium tabular-nums">{{ getCurrentTime() }}</span>
      </div>
      <div class="h-3.5 w-[1px] bg-slate-200 dark:bg-slate-700"></div>
      <div class="flex items-center gap-1.5">
        <div class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
        <span class="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">Operational</span>
      </div>
    </div>

    <!-- Right: Theme toggle + Profile -->
    <div class="flex items-center gap-3">

      <!-- Dark Mode Toggle -->
      <button (click)="theme.toggle()"
        class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        [title]="theme.isDark() ? 'Switch to light mode' : 'Switch to dark mode'">
        @if (theme.isDark()) {
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/>
        </svg>
        } @else {
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
        </svg>
        }
      </button>

      <!-- Profile -->
      <div class="profile relative">
        <button
          class="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
          (click)="onProfileClick()">
          <!-- Avatar -->
          <div class="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 bg-primary-800">
            {{ userData.user.firstName.charAt(0) }}{{ userData.user.lastName.charAt(0) }}
          </div>
          <!-- Name -->
          <div class="hidden md:block text-left">
            <p class="text-[11px] font-bold text-slate-800 dark:text-slate-100 leading-tight">
              {{ userData.user.firstName }} {{ userData.user.lastName }}
            </p>
            <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{{ userData.user.roles[0].name || '—' }}</p>
          </div>
          <!-- Chevron -->
          <svg class="w-3 h-3 text-slate-400 transition-transform duration-200 group-hover:text-slate-600 dark:text-slate-400"
            [class.rotate-180]="isProfileClicked" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        <!-- Dropdown -->
        @if (isProfileClicked) {
        <div class="fixed inset-0 z-40" (click)="onProfileClick()"></div>
        <div class="absolute right-0 top-full mt-2 w-52 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
          <!-- User Info Header -->
          <div class="px-4 py-3 bg-slate-50/80 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700">
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 bg-primary-800">
                {{ userData.user.firstName.charAt(0) }}{{ userData.user.lastName.charAt(0) }}
              </div>
              <div>
                <p class="text-xs font-bold text-slate-800 dark:text-slate-100 leading-tight">{{ userData.user.firstName }} {{ userData.user.lastName }}</p>
                <p class="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{{ userData.user.roles[0].name || '—' }}</p>
              </div>
            </div>
          </div>

          <!-- Menu Items -->
          <div class="py-1">
            <button (click)="onChangePasswordClick()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors border-b border-slate-50 dark:border-slate-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
              </svg>
              <span>Change Password</span>
            </button>

            <button (click)="onLogOutClicked()"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
        }
      </div>

    </div>

  </div>
</section>

<!-- Security Modal -->
@if (isChangePasswordOpen) {
<div
  class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300"
  (click)="onCloseChangePassword()">
  <div
    class="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.14),0_0_0_1px_rgba(0,0,0,0.02)] overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
    (click)="$event.stopPropagation()">
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-800/40 via-primary-800 to-primary-800/40"></div>

    <div class="flex items-center justify-between px-10 py-7 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2 h-2 rounded-full bg-primary-800 animate-pulse"></div>
          <h3 class="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-widest">Change Password</h3>
        </div>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Update your security credentials</p>
      </div>
      <button (click)="onCloseChangePassword()"
        class="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700 hover:shadow-lg rounded-full transition-all duration-300 group">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
          class="group-hover:rotate-90 transition-transform duration-300">
          <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
        </svg>
      </button>
    </div>
    <div class="p-10">
      <app-change-password (closePassword)="onCloseChangePassword()"></app-change-password>
    </div>
  </div>
</div>
}

<!-- Logout Confirmation -->
@if (showLogoutDialog) {
<app-confirmation-dialog [title]="'Sign Out'" [message]="'Are you sure you want to end your current session?'"
  [itemName]="''" [confirmText]="'Sign Out'" [cancelText]="'Stay'" [type]="'primary'" (onConfirm)="confirmLogout()"
  (onCancel)="cancelLogout()"></app-confirmation-dialog>
}
